import type { PrettyFn, PrettyLogger } from './types';
import {
    createPrettyLogger,
    type CreatePrettyLoggerOptions,
} from './factories';
import {
    type ILoggerCallback,
    LogLevel,
    type LoggerOptions,
} from '@azure/msal-common';

export const pMsal: PrettyFn = ($) =>
    $('MSAL', $.bold, $.fg('blue', 'dark'), $.italic);
export const pMsalContainsPii: PrettyFn = ($) =>
    $('[Contains Pii]', $.italic, $.sm);
export const pMsalPackage: (
    packageName: string,
    packageVersion: string
) => PrettyFn = (packageName: string, packageVersion: string) => ($) =>
    $.s(packageName, $.sm, $.fg('blue', 'light'), $.italic)
        .s('@', $.sm, $.fg('blue', 'light'), $.bold)
        .s(packageVersion, $.sm, $.fg('blue', 'light'), $.italic);
export const pMsalMessageString: (msg: string) => PrettyFn =
    (msg: string) => ($) =>
        $.s(msg);
export const pMsalLogId: (logId: string) => PrettyFn = (logId: string) => ($) =>
    $.s(logId, $.xxs, $.bold, $.fg('blue', 'light'));

export interface MsalLoggerCallbackOptions extends CreatePrettyLoggerOptions {
    pretty?: PrettyLogger;
    showPii?: boolean;
}

export const msalMsgRegex =
    /^ ?\[(?<timestamp>[^\]]*)\](?: : \[(?<logId>[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})\])?(?: : (?<package>(?<packageName>@?[^@ ]*))@(?<packageVersion>[0-9]+(?:\.[0-9]+)?(?:\.[0-9]+)?(?:-[a-zA-Z._-])?))? : (?:(?<level>[a-zA-Z]+) - )?(?<message>.*)/;

export function createMsalLoggerCallback(
    options: MsalLoggerCallbackOptions = {}
): ILoggerCallback {
    const prettyLogger =
        options?.pretty ?? console.pretty ?? createPrettyLogger(options);
    const showPii = options?.showPii ?? false;

    function getMsg(message: string, containsPii: boolean): PrettyFn {
        if (!showPii && containsPii) return pMsalContainsPii;

        const match = msalMsgRegex.exec(message);

        if (!match || !match[0]) return ($) => $.s(message, $.italic);

        const { groups } = match;

        return ($) => {
            let result = $.s('[', $.sm, $.bold, $.fg('blue', 'light'));

            if (groups?.logId)
                result = result
                    .p(pMsalLogId(groups.logId))
                    .s(' : ', $.sm, $.fg('blue', 'light'), $.bold);
            if (groups?.packageName && groups?.packageVersion) {
                result = result.p(
                    pMsalPackage(groups.packageName, groups.packageVersion)
                );
            }

            result = result
                .s(']', $.sm, $.bold, $.fg('blue', 'light'))
                .s(' : ', $.dim)
                .p(pMsalMessageString(groups?.message ?? 'no-msg'));
            return result;
        };
    }

    return (level: LogLevel, message: string, containsPii: boolean) => {
        const msg = getMsg(message, containsPii);
        switch (level) {
            case LogLevel.Error:
                prettyLogger.error`${pMsal} ${msg}`();
                return;
            case LogLevel.Warning:
                prettyLogger.warn`${pMsal} ${msg}`();
                return;
            case LogLevel.Info:
                prettyLogger.info`${pMsal} ${msg}`();
                return;
            case LogLevel.Verbose:
                prettyLogger.log`${pMsal} ${msg}`();
                return;
            case LogLevel.Trace:
                prettyLogger.debug`${pMsal} ${msg}`();
                return;
        }
    };
}

export interface MsalLoggerOptions
    extends LoggerOptions,
        MsalLoggerCallbackOptions {
    debugMode?: boolean;
}

export function createMsalLoggerOptions(
    options: MsalLoggerOptions
): LoggerOptions {
    return {
        loggerCallback: createMsalLoggerCallback({
            ...options,
            showPii: options.showPii ?? options.debugMode ?? false,
        }),
        ...options,
        piiLoggingEnabled:
            options.piiLoggingEnabled ?? options.showPii ?? options.debugMode,
        logLevel:
            options.logLevel ??
            (options.debugMode ? LogLevel.Verbose : LogLevel.Warning),
    };
}
